import { DatePipe, NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { Router } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { NgxSpinnerService } from 'ngx-spinner';
import { ApiServiceService } from '../api-service.service';
import { CommonInfoTooltipComponent } from '../common-component-info-tooltip/common-component-info-tooltip.component';
import { CommonPaginationComponent } from '../common-component-pagination/common-component-pagination.component';
import { CommonSearchComponent } from '../common-component-search/common-component-search.component';
import { FotaInfoComponent } from '../fota-info/fota-info.component';
import { batchProgressKpiInfo } from '../shared/info-text/batch-progress-kpi.info';
import { SharedValidators } from '../shared/validators/shared-validators';
import { MatTooltip } from '@angular/material/tooltip';
import { IndianNumberPipe } from '../pipes/indian-number.pipe';

@Component({
  selector: 'app-fota-batch-details',
  templateUrl: './fota-batch-details.component.html',
  styleUrls: ['./fota-batch-details.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatSelect,
    MatOption,
    MatIcon,
    NgClass,
    NgxPaginationModule,
    DatePipe,
    CommonSearchComponent,
    CommonPaginationComponent,
    MatTooltip,
    IndianNumberPipe,
    CommonInfoTooltipComponent,
    FotaInfoComponent,
  ],
})
export class FotaBatchDetailsComponent implements OnInit {
  page = 1;
  size = 100;
  totalCount = 0;
  searchField = '';
  version = '';
  state = '';

  deviceFotaForm: FormGroup = this.formBuilder.group({});
  deviceFotaFilterForm: FormGroup = this.formBuilder.group({});
  file: File | null = null;

  deviceInitiationArray = [];
  date: Date = new Date();

  kpiSelected = 'All';

  filter = false;
  preDefinedRefId = '';
  fotaBatchFotaDeviceDetails = [];
  fotaBatchDetails;

  fotaBatchRefId = '';
  viewMode = false;
  userPermission = [];
  loggedInUserId = '';
  viewFota = false;
  deleteFota = false;
  updateFota = false;
  createFota = false;
  NonInitiated = 0;
  totalAborted = 0;
  totalCompleted = 0;
  totalInitiated = 0;
  totalInProgress = 0;
  totalUploaded = 0;

  // Plain-language explanations shown by the info-tooltip icons on each KPI
  // card, kept in a shared file (imported above) since FOTA and OTA batch
  // pages track the same device-progress concepts for isolation from this
  // component's logic.
  kpiInfo = batchProgressKpiInfo;

  constructor(
    public apiMicroservice: ApiServiceService,
    public router: Router,
    private formBuilder: FormBuilder,
    public datePipe: DatePipe,
    private spinner: NgxSpinnerService
  ) {}

  ngOnInit(): void {
    this.checkPermission();
    if (this.viewFota) {
      this.setDeviceFotaForm();

      this.fotaBatchRefId = sessionStorage.getItem('fotaBatchRefId') || '';
      if (this.fotaBatchRefId === 'noId') {
        const latest_date = this.datePipe.transform(
          this.date,
          'dd MMM yyyy hh:mm:ss a'
        );
        this.deviceFotaForm.controls['createdAt'].setValue(latest_date);
      } else {
        this.fetchFotaBatchDetails(this.fotaBatchRefId);
      }
    }
  }

  checkPermission() {
    this.loggedInUserId = this.apiMicroservice.decryptData(
      sessionStorage.getItem('userRefId') || ''
    );
    this.userPermission = JSON.parse(
      this.apiMicroservice.decryptData(
        sessionStorage.getItem('userPermission') || '[]'
      )
    );
    this.userPermission.forEach(
      (element: {
        pageName: string;
        view: boolean;
        create: boolean;
        delete: boolean;
        update: boolean;
      }) => {
        if (element.pageName === 'FOTA Batch' && element.view === false) {
          this.navigateBack();
        }
        if (element.pageName === 'FOTA Batch' && element.view === true) {
          this.viewFota = true;
        }
        if (element.pageName === 'FOTA Batch' && element.delete === true) {
          this.deleteFota = true;
        }
        if (element.pageName === 'FOTA Batch' && element.update === true) {
          this.updateFota = true;
        }
        if (element.pageName === 'FOTA Batch' && element.create === true) {
          this.createFota = true;
        }
      }
    );
  }

  setDeviceFotaForm() {
    this.deviceFotaForm = this.formBuilder.group({
      batchId: new FormControl(''),
      name: new FormControl('', [SharedValidators.requiredAndNoEmptySpaces]),
      createdAt: new FormControl(''),
      ais140: new FormControl(false),
      isFotaMandatory: new FormControl(false),
      description: new FormControl('', [
        SharedValidators.requiredAndNoEmptySpaces,
      ]),
      fileName: ['', [Validators.required]],
    });

    this.deviceFotaFilterForm = this.formBuilder.group({
      state: new FormControl(''),
      version: new FormControl(''),
    });
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const selectedFile = input.files[0];
      this.file = selectedFile; // Store the selected file
      // Update form fields
      this.deviceFotaForm.patchValue({
        fileName: selectedFile.name, // Store file name
      });
    } else {
      this.file = null;
      // Reset values if no file is selected
      this.deviceFotaForm.patchValue({
        fileName: '',
      });
    }
  }

  SubmitBatch() {
    if (this.file) {
      if (this.deviceFotaForm.invalid) {
        this.deviceFotaForm?.get('name')?.markAsTouched();
        this.deviceFotaForm?.get('createdAt')?.markAsTouched();
        this.deviceFotaForm?.get('description')?.markAsTouched();
      } else {
        const payload2 = this.deviceFotaForm.value;
        payload2.createdBy = this.loggedInUserId;
        this.createFotaBatch(payload2, this.file);
      }
    } else {
      this.apiMicroservice.openSnackbar('Please select the file');
    }
  }

  createFotaBatch(payload, file) {
    this.spinner.show();
    this.apiMicroservice.createFotaBatch(payload, file).subscribe({
      next: (res) => {
        this.spinner.hide();
        if (res.status) {
          this.apiMicroservice.openSnackbar(res.message);
          this.preDefinedRefId = res.data._id;
          sessionStorage.setItem('fotaBatchRefId', res.data._id);
          this.deviceFotaForm.controls['batchId'].setValue(
            res.data.fotaBatchId
          );
          this.viewMode = true;
          this.fetchFotaBatchDetails(this.preDefinedRefId);
          this.getFotaBatchDeviceDetails();
        } else {
          this.apiMicroservice.openSnackbar(res.message);
        }
        this.spinner.hide();
      },
      error: (error) => {
        this.apiMicroservice.openSnackbar(
          `ERROR :>> ${error.error.message || 'Something went wrong'}`
        );
        this.spinner.hide();
      },
    });
  }

  fetchFotaBatchDetails(id: string) {
    this.spinner.show();
    this.apiMicroservice.fetchFotaBatchDetailsById(id).subscribe((res) => {
      if (res.status) {
        if (res.data.length > 0) {
          this.filter = true;
          this.fotaBatchDetails = res.data[0];
          const data = res.data[0];
          this.viewMode = true;
          const changedFormatBatchDate = this.datePipe.transform(
            data.createdAt,
            'dd MMM yyyy hh:mm:ss a'
          );
          this.deviceFotaForm = this.formBuilder.group({
            batchId: new FormControl(data.batchId),
            name: new FormControl(data.name, [
              SharedValidators.requiredAndNoEmptySpaces,
            ]),
            createdAt: new FormControl(changedFormatBatchDate, []),
            ais140: new FormControl(data.ais140 || false),
            isFotaMandatory: new FormControl(data.isFotaMandatory || false),
            description: new FormControl(data.description, [
              SharedValidators.requiredAndNoEmptySpaces,
            ]),
          });
          this.deviceFotaForm.get('ais140')?.disable();
          this.deviceFotaForm.get('isFotaMandatory')?.disable();
          this.preDefinedRefId = data._id;
          this.getFotaBatchDeviceDetails();
        } else {
          this.apiMicroservice.openSnackbar('Data not found');
        }
      } else {
        this.apiMicroservice.openSnackbar('Something went wrong');
      }
    });
  }

  changeKPISelected(kpiSelected: string) {
    this.kpiSelected = kpiSelected;
    this.page = 1;
    this.size = 100;
    this.fetchFotaBatchDetails(this.fotaBatchRefId);
  }

  getFotaBatchDeviceDetails() {
    this.spinner.show();
    this.apiMicroservice
      .fotaBatchDeviceListByBatchId(
        this.page,
        this.size,
        this.preDefinedRefId,
        this.version,
        this.state,
        this.searchField,
        this.kpiSelected
      )
      .subscribe((res) => {
        if (res.status) {
          this.spinner.hide();
          this.fotaBatchFotaDeviceDetails = res.data.batchDeviceData;

          this.totalUploaded = res.totalUploaded;
          this.NonInitiated = res.NonInitiated;
          this.totalAborted = res.totalAborted;
          this.totalCompleted = res.totalCompleted;
          this.totalInProgress = res.totalInProgress;
          // this.totalInitiated = res.totalInitiated;

          this.totalCount = res.data.totalItems;
          this.page = res.data.pageNumber;

          this.apiMicroservice.openSnackbar('Fetched Successfully');
        } else {
          this.spinner.hide();
          this.apiMicroservice.openSnackbar('Something went wrong');
        }
      });
  }

  onSearch(event: string) {
    this.page = 1;
    this.searchField = event;
    this.getFotaBatchDeviceDetails();
  }

  onPageChange(event: number) {
    this.page = event;
    this.getFotaBatchDeviceDetails();
  }

  onSizeChange(event: number) {
    this.page = 1;
    this.size = event;
    this.getFotaBatchDeviceDetails();
  }

  onFilterChange() {
    this.page = 1;
    const payload = this.deviceFotaFilterForm.value;

    this.version = payload.version;
    this.state = payload.state;
    this.getFotaBatchDeviceDetails();
    this.closeModal();
  }

  filterDropdownMenus() {
    document
      ?.getElementById('filterDetails')
      ?.parentElement?.classList.add('active');
  }

  closeModal() {
    document
      ?.getElementById('filterDetails')
      ?.parentElement?.classList.remove('active');
  }

  clearFilter() {
    this.deviceFotaFilterForm.reset();
    this.deviceFotaFilterForm.controls['state'].setValue('');
    this.deviceFotaFilterForm.controls['version'].setValue('');
    this.fetchFotaBatchDetails(this.fotaBatchRefId);
  }

  startFota() {
    this.apiMicroservice
      .startFota(this.preDefinedRefId, this.loggedInUserId)
      .subscribe((res) => {
        if (res.status) {
          this.apiMicroservice.openSnackbar('Fota Started Successfully');
          document.getElementById('startFotaButton').style.display = 'none';
        } else {
          this.apiMicroservice.openSnackbar('Something went wrong');
        }
      });
  }

  abort(id: string) {
    if (confirm('Are you sure to Abort the device FOTA?')) {
      this.spinner.show();
      const abortedBy = this.loggedInUserId;
      this.apiMicroservice.abortFotaDevice(id, abortedBy).subscribe({
        next: (res) => {
          if (res.status) {
            this.apiMicroservice.openSnackbar('Aborted Successfully');
            this.ngOnInit();
          } else {
            this.apiMicroservice.openSnackbar('Something went wrong');
          }
        },
        error: (ERROR: HttpErrorResponse) => {
          this.spinner.hide();
          const example = ERROR.error.message;
          this.apiMicroservice.openSnackbar(example);
          this.ngOnInit();
        },
      });
    }
  }

  onChangeStateSelect(deviceValue: { value: string }) {
    if (deviceValue.value === 'withoutState') {
      this.deviceFotaForm.controls['state'].setValue('');
      this.deviceFotaForm.controls['state'].disable();
    } else if (deviceValue.value === 'withState') {
      this.deviceFotaForm.controls['state'].setValue('');
      this.deviceFotaForm.controls['state'].enable();
    } else if (deviceValue.value === '') {
      this.deviceFotaForm.controls['state'].setValue('');
      this.deviceFotaForm.controls['state'].enable();
    }
  }

  downloadMyFile(): void {
    const link: HTMLAnchorElement = document.createElement('a');
    link.setAttribute('target', '_blank');
    link.setAttribute(
      'href',
      '../../assets/downloadableSampleFiles/Sample_FOTA_Sheet.csv'
    );
    link.setAttribute('download', 'Sample_FOTA_Sheet.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  getGovtIp1Tooltip(device: {
    govtIp1SetStatus: string;
    govtIp1ProcessedAt: string;
    govtIp1: string;
    govtPort1: string;
  }): string {
    if (!device.govtIp1SetStatus) {
      return 'No Govt IP configured';
    }

    const date = device.govtIp1ProcessedAt
      ? new Date(device.govtIp1ProcessedAt).toLocaleString()
      : '--';

    return `Govt IP : ${device.govtIp1 || '--'}
Port    : ${device.govtPort1 || '--'}
Processed At: ${date}`;
  }

  getGovtIp2Tooltip(device: {
    govtIp2SetStatus: string;
    govtIp2ProcessedAt: string;
    govtIp2: string;
    govtPort2: string;
  }): string {
    if (!device.govtIp2SetStatus) {
      return 'No Govt IP configured';
    }

    const date = device.govtIp2ProcessedAt
      ? new Date(device.govtIp2ProcessedAt).toLocaleString()
      : '--';

    return `Govt IP : ${device.govtIp2 || '--'}
Port    : ${device.govtPort2 || '--'}
Processed At: ${date}`;
  }

  getGovtIp3Tooltip(device: {
    govtIp3SetStatus: string;
    govtIp3ProcessedAt: string;
    govtIp3: string;
    govtPort3: string;
  }): string {
    if (!device.govtIp3SetStatus) {
      return 'No Govt IP configured';
    }

    const date = device.govtIp3ProcessedAt
      ? new Date(device.govtIp3ProcessedAt).toLocaleString()
      : '--';

    return `Govt IP : ${device.govtIp3 || '--'}
Port    : ${device.govtPort3 || '--'}
Processed At: ${date}`;
  }

  getGovtIp4Tooltip(device: {
    govtIp4SetStatus: string;
    govtIp4ProcessedAt: string;
    govtIp4: string;
    govtPort4: string;
  }): string {
    if (!device.govtIp4SetStatus) {
      return 'No Govt IP configured';
    }

    const date = device.govtIp4ProcessedAt
      ? new Date(device.govtIp4ProcessedAt).toLocaleString()
      : '--';

    return `Govt IP : ${device.govtIp4 || '--'}
Port    : ${device.govtPort4 || '--'}
Processed At: ${date}`;
  }

  get1stSEOTooltip(device: {
    stateEnableSetStatus: string;
    stateEnableProcessedAt: string;
    stateEnable: string;
  }): string {
    if (!device.stateEnableSetStatus) {
      return 'No Govt IP configured';
    }

    const date = device.stateEnableProcessedAt
      ? new Date(device.stateEnableProcessedAt).toLocaleString()
      : '--';

    return `1st SEO Command : ${device.stateEnable || '--'}
Processed At: ${date}`;
  }

  get2ndSEOTooltip(device: {
    secondaryStateEnableSetStatus: string;
    secondaryStateEnableProcessedAt: string;
    secondaryStateEnable: string;
  }): string {
    if (!device.secondaryStateEnableSetStatus) {
      return 'No Govt IP configured';
    }

    const date = device.secondaryStateEnableProcessedAt
      ? new Date(device.secondaryStateEnableProcessedAt).toLocaleString()
      : '--';

    return `2nd SEO Command : ${device.secondaryStateEnable || '--'}
Processed At: ${date}`;
  }

  getPingCountTooltip(device: { pingCount: string }): string {
    return `Number of times the device resumed FOTA download: ${device.pingCount || '--'}`;
  }

  downloadDurationTooltip(device: {
    startedAt: Date;
    completedAt: Date;
  }): string {
    return `${device.startedAt ? new Date(device.startedAt).toLocaleString() : '--'} 
    to  ${device.completedAt ? new Date(device.completedAt).toLocaleString() : '--'}`;
  }

  getRetryCountTooltip(device: {
    attemptCount: string;
    attempts?: {
      attemptNumber: number;
      startedAt: string | Date;
      completedAt: string | Date | null;
      outcome: string | null;
    }[];
  }): string {
    const header = `FOTA Download Attempts: ${device.attemptCount || '--'}`;
    if (!device.attempts || device.attempts.length === 0) {
      return header;
    }
    const lines = device.attempts.map((attempt) => {
      const start = attempt.startedAt
        ? new Date(attempt.startedAt).toLocaleString()
        : '--';
      const end = attempt.completedAt
        ? new Date(attempt.completedAt).toLocaleString()
        : 'in progress';
      const outcome = attempt.outcome || 'in progress';
      return `#${attempt.attemptNumber}: ${start} → ${end}  (${outcome})`;
    });
    return `${header}\n${lines.join('\n')}`;
  }

  navigateBack() {
    this.apiMicroservice.back();
  }

  redirect(to: string): void {
    this.router.navigate([to]);
  }
}
